import React from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { formatDistanceToNowStrict } from 'date-fns';
import {
  Activity,
  Car,
  UserCheck,
  Users,
  WifiOff,
  Clock,
} from 'lucide-react';
import { api } from '../../../utils/api';

/**
 * The first thing the Dashboard shows: who is on the road right now.
 *
 *   - driver counts, split Acting / Ride: online, available, on a trip, silent, total
 *   - trips right now and today, split Acting / Ride
 *   - the drivers currently active, newest signal first
 *
 * "Online" here means the driver's phone has reported a location in the last
 * `liveWindowMinutes` (the server decides, see GET /overview/live). A driver who is
 * still marked online but has gone quiet is "silent", not online — that is the
 * difference between this and a plain count of the stored status.
 *
 * Refreshes itself every 10 seconds. Follows the page's regional office and developer
 * trip filters.
 */

const colorClasses = {
  primary: 'text-primary-600 bg-primary-50',
  success: 'text-success-600 bg-success-50',
  warning: 'text-warning-600 bg-warning-50',
  danger: 'text-danger-600 bg-danger-50',
  info: 'text-blue-600 bg-blue-50',
};

const Tile = ({ title, value, hint, icon: Icon, color = 'primary', loading }) => (
  <div className="stats-card p-5">
    <div className="flex items-center justify-between mb-3">
      <h3 className="stats-card-label">{title}</h3>
      <div className={`p-2 rounded-lg ${colorClasses[color] || colorClasses.primary}`}>
        <Icon className="h-5 w-5" />
      </div>
    </div>
    {loading ? (
      <div className="animate-pulse space-y-2">
        <div className="h-8 bg-gray-200 rounded w-16" />
        <div className="h-3 bg-gray-200 rounded w-32" />
      </div>
    ) : (
      <>
        <div className="stats-card-value mb-1">{Number(value || 0).toLocaleString()}</div>
        {hint && <div className="text-xs text-gray-500">{hint}</div>}
      </>
    )}
  </div>
);

const split = (acting, ride) => `Acting ${acting ?? 0} · Ride ${ride ?? 0}`;

const Row = ({ label, acting, normal }) => (
  <tr className="border-t border-gray-100">
    <td className="py-2 text-sm text-gray-600">{label}</td>
    <td className="py-2 text-sm text-right font-medium text-gray-900">{acting ?? 0}</td>
    <td className="py-2 text-sm text-right font-medium text-gray-900">{normal ?? 0}</td>
  </tr>
);

const Badge = ({ children, tone }) => (
  <span className={`inline-block px-2 py-0.5 text-xs rounded-full ${tone}`}>{children}</span>
);

const lastSeenText = (ms) => {
  if (!ms) return '—';
  try {
    return `${formatDistanceToNowStrict(new Date(ms))} ago`;
  } catch (e) {
    return '—';
  }
};

const LiveOverview = ({ developerFilter, regionalOffice }) => {
  const { data, isLoading, isError, dataUpdatedAt } = useQuery(
    ['dashboard-live', developerFilter, regionalOffice],
    () => api.get('/overview/live', { params: { developerFilter, regionalOffice } }),
    {
      refetchInterval: 10000,
      staleTime: 5000,
      select: (res) => res?.data?.data,
    }
  );

  const drivers = data?.drivers || {};
  const all = drivers.all || {};
  const acting = drivers.acting || {};
  const ride = drivers.ride || {};
  const trips = data?.trips || {};
  const live = trips.active || {};
  const today = trips.today || {};
  const activeDrivers = data?.activeDrivers || [];
  const windowMin = data?.liveWindowMinutes ?? 15;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Live overview</h2>
          <p className="text-sm text-gray-500">
            Drivers and trips right now. A driver counts as online when their phone has sent a
            location in the last {windowMin} minutes.
          </p>
        </div>
        <div className="text-xs text-gray-400 flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {isError ? 'Could not refresh' : dataUpdatedAt ? `Updated ${new Date(dataUpdatedAt).toLocaleTimeString()}` : 'Loading…'}
        </div>
      </div>

      {/* Drivers */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Tile
          title="Online now"
          value={all.online}
          hint={split(acting.online, ride.online)}
          icon={Activity}
          color="success"
          loading={isLoading}
        />
        <Tile
          title="Available"
          value={all.available}
          hint={`${split(acting.available, ride.available)} · waiting for a trip`}
          icon={UserCheck}
          color="primary"
          loading={isLoading}
        />
        <Tile
          title="On a trip"
          value={all.onTrip}
          hint={split(acting.onTrip, ride.onTrip)}
          icon={Car}
          color="info"
          loading={isLoading}
        />
        <Tile
          title="Online, no signal"
          value={all.silent}
          hint={`Marked online, no location for ${windowMin}+ min`}
          icon={WifiOff}
          color="warning"
          loading={isLoading}
        />
        <Tile
          title="Total drivers"
          value={all.total}
          hint={`${split(acting.total, ride.total)} · ${all.offline ?? 0} offline`}
          icon={Users}
          color="primary"
          loading={isLoading}
        />
      </div>

      {/* Trips */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Trips right now</h3>
          </div>
          <div className="card-body">
            <table className="w-full">
              <thead>
                <tr className="text-xs text-gray-500">
                  <th className="text-left font-medium pb-1" />
                  <th className="text-right font-medium pb-1">Acting</th>
                  <th className="text-right font-medium pb-1">Ride</th>
                </tr>
              </thead>
              <tbody>
                <Row label="Running" acting={live.acting?.running} normal={live.normal?.running} />
                <Row label="Driver assigned" acting={live.acting?.booked} normal={live.normal?.booked} />
                <Row label="Finding a driver" acting={live.acting?.searching} normal={live.normal?.searching} />
                <Row label="Ended, payment due" acting={live.acting?.awaitingPayment} normal={live.normal?.awaitingPayment} />
                <Row label="Scheduled (upcoming)" acting={live.acting?.scheduled} normal={live.normal?.scheduled} />
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Today</h3>
          </div>
          <div className="card-body">
            <table className="w-full">
              <thead>
                <tr className="text-xs text-gray-500">
                  <th className="text-left font-medium pb-1" />
                  <th className="text-right font-medium pb-1">Acting</th>
                  <th className="text-right font-medium pb-1">Ride</th>
                </tr>
              </thead>
              <tbody>
                <Row label="Trips" acting={today.acting?.total} normal={today.normal?.total} />
                <Row label="Completed" acting={today.acting?.completed} normal={today.normal?.completed} />
                <Row label="Cancelled" acting={today.acting?.cancelled} normal={today.normal?.cancelled} />
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Active drivers */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Currently active drivers</h3>
          <span className="text-sm text-gray-500">
            {data?.activeDriversTotal ?? 0}
            {(data?.activeDriversTotal ?? 0) > activeDrivers.length ? ` (showing ${activeDrivers.length})` : ''}
          </span>
        </div>
        <div className="card-body overflow-x-auto">
          {isLoading ? (
            <div className="animate-pulse h-16 bg-gray-100 rounded" />
          ) : activeDrivers.length === 0 ? (
            <div className="text-sm text-gray-500 py-4">
              No driver is online right now.
              {(all.silent ?? 0) > 0 && ` ${all.silent} marked online have not sent a location in ${windowMin}+ minutes.`}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 text-left">
                  <th className="font-medium pb-2">Driver</th>
                  <th className="font-medium pb-2">Type</th>
                  <th className="font-medium pb-2">Status</th>
                  <th className="font-medium pb-2">Office</th>
                  <th className="font-medium pb-2 text-right">Last seen</th>
                </tr>
              </thead>
              <tbody>
                {activeDrivers.map((d) => (
                  <tr key={d.id} className="border-t border-gray-100">
                    <td className="py-2">
                      <Link to={`/drivers/${d.id}`} className="font-medium text-primary-600 hover:underline">{d.name}</Link>
                      <div className="text-xs text-gray-500">{d.phone}</div>
                    </td>
                    <td className="py-2 space-x-1">
                      {d.acting && <Badge tone="bg-purple-50 text-purple-700">Acting</Badge>}
                      {d.ride && <Badge tone="bg-blue-50 text-blue-700">Ride</Badge>}
                    </td>
                    <td className="py-2">
                      {d.state === 'on_trip'
                        ? <Badge tone="bg-blue-50 text-blue-700">On a trip</Badge>
                        : <Badge tone="bg-green-50 text-green-700">Available</Badge>}
                    </td>
                    <td className="py-2 text-gray-600">{d.regionalOfficeName || '—'}</td>
                    <td className="py-2 text-right text-gray-600">{lastSeenText(d.lastSeen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default LiveOverview;
